import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ToolLayout } from '../ToolLayout';

const CHARS = {
  upper:   'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lower:   'abcdefghijklmnopqrstuvwxyz',
  digits:  '0123456789',
  symbols: '!@#$%^&*()-_=+[]{}|;:,.<>?',
};

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', gap: theme.spacing(3), alignItems: 'flex-start', flexWrap: 'wrap' },
  settingsPane: { flex: '0 0 280px', display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  outputPane: { flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: theme.spacing(0.5),
  },
  passwordBox: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '0.05em',
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    wordBreak: 'break-all',
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
  },
  strengthBar: {
    height: 6,
    borderRadius: 3,
    transition: 'background 0.3s',
  },
  historyItem: {
    fontFamily: '"Fira Code","Consolas",monospace',
    fontSize: 12.5,
    padding: theme.spacing(0.75, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    wordBreak: 'break-all',
    backgroundColor: theme.palette.background.paper,
  },
}));

interface Options {
  length: number;
  upper: boolean;
  lower: boolean;
  digits: boolean;
  symbols: boolean;
  excludeAmbiguous: boolean;
  count: number;
}

function generatePassword(opts: Options): string | null {
  let pool = '';
  if (opts.upper)   pool += opts.excludeAmbiguous ? CHARS.upper.replace(/[OI]/g, '')   : CHARS.upper;
  if (opts.lower)   pool += opts.excludeAmbiguous ? CHARS.lower.replace(/[oil]/g, '')  : CHARS.lower;
  if (opts.digits)  pool += opts.excludeAmbiguous ? CHARS.digits.replace(/[01]/g, '')  : CHARS.digits;
  if (opts.symbols) pool += CHARS.symbols;
  if (!pool) return null;

  const arr = new Uint32Array(opts.length);
  crypto.getRandomValues(arr);
  return Array.from(arr, v => pool[v % pool.length]).join('');
}

function strength(pw: string): { label: string; color: string; pct: number } {
  if (!pw) return { label: '', color: 'transparent', pct: 0 };
  let score = 0;
  if (pw.length >= 12) score++;
  if (pw.length >= 20) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: '#f44336', pct: 25 };
  if (score <= 3) return { label: 'Fair', color: '#ff9800', pct: 50 };
  if (score <= 4) return { label: 'Good', color: '#2196f3', pct: 75 };
  return { label: 'Strong', color: '#4caf50', pct: 100 };
}

export function PasswordGeneratorPage() {
  const { classes } = useStyles();

  const [opts, setOpts] = useState<Options>({
    length: 20,
    upper: true,
    lower: true,
    digits: true,
    symbols: true,
    excludeAmbiguous: false,
    count: 1,
  });
  const [passwords, setPasswords] = useState<string[]>([]);
  const [copied, setCopied] = useState<number | null>(null);

  const atLeastOne = opts.upper || opts.lower || opts.digits || opts.symbols;

  const generate = useCallback((o: Options = opts) => {
    const results: string[] = [];
    for (let i = 0; i < o.count; i++) {
      const pw = generatePassword(o);
      if (pw) results.push(pw);
    }
    setPasswords(results);
    setCopied(null);
  }, [opts]);

  const set = (patch: Partial<Options>) => {
    const next = { ...opts, ...patch };
    setOpts(next);
    if (passwords.length) generate(next);
  };

  const copy = (idx: number) => {
    navigator.clipboard.writeText(passwords[idx]);
    setCopied(idx);
    setTimeout(() => setCopied(c => c === idx ? null : c), 2000);
  };

  const mainPw = passwords[0] ?? '';
  const str = strength(mainPw);

  return (
    <ToolLayout
      title="Password / Token Generator"
      subtitle="Generate strong, cryptographically random passwords and tokens"
    >
      <Box className={classes.layout}>
        {/* ── Settings ── */}
        <Box className={classes.settingsPane}>
          <Typography className={classes.paneLabel}>Options</Typography>

          {/* Length */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Length</Typography>
              <Typography variant="body2" fontWeight={700}>{opts.length}</Typography>
            </Box>
            <Slider
              min={4} max={128} value={opts.length}
              onChange={(_, v) => set({ length: v as number })}
              sx={{ mt: 0.5 }}
            />
          </Box>

          {/* Count */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Generate</Typography>
              <Typography variant="body2" fontWeight={700}>{opts.count} {opts.count === 1 ? 'password' : 'passwords'}</Typography>
            </Box>
            <Slider
              min={1} max={20} value={opts.count}
              onChange={(_, v) => set({ count: v as number })}
              sx={{ mt: 0.5 }}
            />
          </Box>

          <Divider />

          {/* Character sets */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox checked={opts.upper} onChange={e => set({ upper: e.target.checked })} size="small" />}
              label={<Typography variant="body2">Uppercase (A–Z)</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={opts.lower} onChange={e => set({ lower: e.target.checked })} size="small" />}
              label={<Typography variant="body2">Lowercase (a–z)</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={opts.digits} onChange={e => set({ digits: e.target.checked })} size="small" />}
              label={<Typography variant="body2">Digits (0–9)</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={opts.symbols} onChange={e => set({ symbols: e.target.checked })} size="small" />}
              label={<Typography variant="body2">Symbols (!@#$…)</Typography>}
            />
          </Box>

          <Divider />

          <FormControlLabel
            control={<Checkbox checked={opts.excludeAmbiguous} onChange={e => set({ excludeAmbiguous: e.target.checked })} size="small" />}
            label={<Typography variant="body2">Exclude ambiguous chars (0, O, I, l, 1)</Typography>}
          />

          {!atLeastOne && (
            <Alert severity="warning" sx={{ mt: 1 }}>Select at least one character set.</Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => generate()}
            disabled={!atLeastOne}
            sx={{ textTransform: 'none', mt: 1 }}
            fullWidth
          >
            Generate
          </Button>
        </Box>

        {/* ── Output ── */}
        <Box className={classes.outputPane}>
          {passwords.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              Configure options and click Generate.
            </Typography>
          )}

          {passwords.length > 0 && (
            <>
              {/* Primary password + strength */}
              <Box>
                <Typography className={classes.paneLabel}>
                  {opts.count === 1 ? 'Generated Password' : 'Generated Passwords'}
                </Typography>

                {opts.count === 1 && (
                  <>
                    <Box className={classes.passwordBox}>
                      <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', flex: 1, wordBreak: 'break-all' }}>
                        {mainPw}
                      </Typography>
                      <Tooltip title={copied === 0 ? 'Copied!' : 'Copy'}>
                        <IconButton size="small" onClick={() => copy(0)} sx={{ flexShrink: 0 }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Strength meter */}
                    <Box sx={{ mt: 1 }}>
                      <Box
                        className={classes.strengthBar}
                        sx={{ width: `${str.pct}%`, backgroundColor: str.color }}
                      />
                      <Typography variant="caption" sx={{ color: str.color, fontWeight: 700 }}>
                        {str.label}
                      </Typography>
                    </Box>
                  </>
                )}

                {opts.count > 1 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {passwords.map((pw, i) => (
                      <Box key={i} className={classes.historyItem}>
                        <Typography sx={{ fontFamily: 'inherit', fontSize: 'inherit', flex: 1, wordBreak: 'break-all' }}>
                          {pw}
                        </Typography>
                        <Tooltip title={copied === i ? 'Copied!' : 'Copy'}>
                          <IconButton size="small" onClick={() => copy(i)} sx={{ flexShrink: 0 }}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => navigator.clipboard.writeText(passwords.join('\n'))}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      Copy all
                    </Button>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ToolLayout>
  );
}
